import { z } from "zod";

export const PREFERENCE_CATEGORIES = [
  "content_categories",
  "content_aesthetic",
  "content_format",
  "campaign_types",
  "campaign_categories",
  "brand_highlights",
  "content_rates",
  "ad_access_rates",
  "interests",
  "collaboration_brands",
  "affiliate_tools",
  "demographics",
] as const;

export type PreferenceCategory = (typeof PREFERENCE_CATEGORIES)[number];

const profileMetadataSchema = z
  .object({
    // Accept ANY string here, not just this app's PREFERENCE_CATEGORIES: the
    // public-profile API recognises a wider legacy set (e.g. `favorite_brands`,
    // `demographics_languages`). Restricting to the enum made a single legacy
    // value fail the whole parse — returning {} and silently dropping every
    // other metadata key on the next save. Preserve the values verbatim.
    hiddenSections: z.array(z.string()).optional(),
  })
  .catchall(
    z.union([z.string(), z.array(z.string()), z.number(), z.boolean()]),
  );

export type ProfileMetadata = z.infer<typeof profileMetadataSchema>;

export function parseProfileMetadata(
  metadata: string | null | undefined,
): ProfileMetadata {
  if (!metadata) return {};

  try {
    const parsed = profileMetadataSchema.safeParse(JSON.parse(metadata));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

export function getHiddenSections(
  metadata: string | null | undefined,
): string[] {
  const parsed = parseProfileMetadata(metadata);
  if (!Array.isArray(parsed.hiddenSections)) return [];
  return parsed.hiddenSections;
}

export function serializeProfileMetadata(
  existing: string | null | undefined,
  updates: Partial<ProfileMetadata>,
): string {
  const parsed = parseProfileMetadata(existing);
  return JSON.stringify({ ...parsed, ...updates });
}
